/* eslint-disable no-nested-ternary */
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CreateIcon from "@mui/icons-material/Create";
import { checkPermission, numberFormatter } from "utils/HelperFunctions";
import TooltipLight from "utils/TooltipLight";
import { PERMISSIONS_NAME } from "lib/constants";
import { styled, useTheme } from "@mui/material/styles";
import UpdateKpiModal from "./UpdateKpiModal";
import Trend from "./Trend";
import "styles/performance.scss";

const TableHeader = styled(TableHead)({
  [`& .MuiTableCell-root p`]: {
    fontSize: "0.9rem",
    fontWeight: "600",
    lineHeight: "1rem",
    color: "dark",
  },
  [`& .MuiTableCell-root`]: {
    padding: "0.75rem 0.5rem",
  },
});

const TableRowSlim = styled(TableRow)({
  [`& .MuiTableCell-root`]: {
    fontSize: "1rem",
    lineHeight: "1.2rem",
    padding: "0.75rem 0.5rem",
  },
});

function descendingComparator(a, b, orderBy) {
  let valueA = a[orderBy];
  let valueB = b[orderBy];
  if (!valueA) return 1;
  if (!valueB) return -1;

  if (["averagePoints", "totalAvg", "totalCount", "totalPoint"].includes(orderBy)) {
    valueA = parseFloat(valueA);
    valueB = parseFloat(valueB);
  } else {
    valueA = valueA?.toLowerCase();
    valueB = valueB?.toLowerCase();
  }
  if (valueB < valueA) {
    return -1;
  }
  if (valueB > valueA) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const SortableLabel = React.memo(
  ({ labelKey, children, order, orderBy, handleRequestSort }) => (
    <TableSortLabel
      active={orderBy === labelKey}
      direction={orderBy === labelKey ? order : "asc"}
      onClick={() => handleRequestSort(labelKey)}
    >
      {children}
      {orderBy === labelKey ? (
        <MDBox component="span" sx={visuallyHidden}>
          {order === "desc" ? "sorted descending" : "sorted ascending"}
        </MDBox>
      ) : null}
    </TableSortLabel>
  ),
  (prevProps, nextProps) =>
    prevProps.order === nextProps.order &&
    prevProps.orderBy === nextProps.orderBy &&
    prevProps.handleRequestSort === nextProps.handleRequestSort
);

function ListingTable(props) {
  const theme = useTheme();
  const [searchByName, setSearchByName] = useState("");
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("averagePoints");
  const { data, dateFilter, fetchUserPerformance, setActiveUserId, activeUserId } = props;
  const loginUser = useSelector((state) => state.loginUser.loginUserState);

  const [selectedDate, setSelectedDate] = useState();
  const [isUpdateKpiModalOpen, setIsUpdateKpiModalOpen] = useState(false);

  const handleDialogClose = (isSave) => {
    setIsUpdateKpiModalOpen(false);
    setSelectedDate();
    if (isSave) {
      fetchUserPerformance();
    }
  };

  useEffect(() => {
    if (!activeUserId && data.length > 0) {
      setActiveUserId(data?.[0]?.id);
    }
    if (activeUserId && !data.find((user) => user.id === activeUserId)) {
      setActiveUserId(data?.[0]?.id);
    }
  }, [data]);

  const handleSearchFilter = (user) => {
    if (!searchByName) {
      return true;
    }
    return user?.name?.toLowerCase().includes(searchByName.toLowerCase());
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const visibleRows = React.useMemo(() => {
    let allDataRecord = [];
    const filteredData = data?.filter(handleSearchFilter);
    const findAllDataIndex = filteredData?.findIndex((obj) => obj.id === "all");
    if (findAllDataIndex > -1) allDataRecord = filteredData?.splice(findAllDataIndex, 1);
    return [...allDataRecord, ...filteredData.sort(getComparator(order, orderBy))];
  }, [order, orderBy, searchByName, handleSearchFilter, data]);

  return (
    <MDBox>
      <MDBox mb={1}>
        <MDInput
          value={searchByName}
          onChange={(event) => setSearchByName(event.target.value)}
          label="Search employee"
        />
      </MDBox>
      <TableContainer
        component={Paper}
        sx={{ borderRadius: "0.5rem" }}
        className="performance-listing-table"
      >
        <Table aria-label="simple table">
          <TableHeader sx={{ display: "table-header-group" }}>
            <TableRow>
              <TableCell width="20%">
                <SortableLabel
                  labelKey="name"
                  order={order}
                  orderBy={orderBy}
                  handleRequestSort={handleRequestSort}
                >
                  <MDTypography>Name</MDTypography>
                </SortableLabel>
              </TableCell>
              <TableCell align="center" sortDirection={orderBy === "averagePoints" ? order : false}>
                <SortableLabel
                  labelKey="averagePoints"
                  order={order}
                  orderBy={orderBy}
                  handleRequestSort={handleRequestSort}
                >
                  <MDTypography>Average Points (Filtered)</MDTypography>
                </SortableLabel>
              </TableCell>
              <TableCell align="center">
                <SortableLabel
                  labelKey="totalAvg"
                  order={order}
                  orderBy={orderBy}
                  handleRequestSort={handleRequestSort}
                >
                  <MDTypography>Average Points (All-time)</MDTypography>
                </SortableLabel>
              </TableCell>
              <TableCell align="center">
                <SortableLabel
                  labelKey="totalCount"
                  order={order}
                  orderBy={orderBy}
                  handleRequestSort={handleRequestSort}
                >
                  <MDTypography>Completed KPIs</MDTypography>
                </SortableLabel>
              </TableCell>
              <TableCell align="center">
                <SortableLabel
                  labelKey="totalPoint"
                  order={order}
                  orderBy={orderBy}
                  handleRequestSort={handleRequestSort}
                >
                  <MDTypography>Achieved Points</MDTypography>
                </SortableLabel>
              </TableCell>
              <TableCell align="center">
                <SortableLabel
                  labelKey="location"
                  order={order}
                  orderBy={orderBy}
                  handleRequestSort={handleRequestSort}
                >
                  <MDTypography>Location</MDTypography>
                </SortableLabel>
              </TableCell>
              <TableCell align="center">
                <SortableLabel
                  labelKey="team"
                  order={order}
                  orderBy={orderBy}
                  handleRequestSort={handleRequestSort}
                >
                  <MDTypography>Team</MDTypography>
                </SortableLabel>
              </TableCell>
              <TableCell align="center">
                <SortableLabel
                  labelKey="role"
                  order={order}
                  orderBy={orderBy}
                  handleRequestSort={handleRequestSort}
                >
                  <MDTypography>Role</MDTypography>
                </SortableLabel>
              </TableCell>
              <TableCell align="center">
                <SortableLabel
                  labelKey="reportsToName"
                  order={order}
                  orderBy={orderBy}
                  handleRequestSort={handleRequestSort}
                >
                  <MDTypography>Reports To</MDTypography>
                </SortableLabel>
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows?.map((row, index) => (
              <TableRowSlim
                key={row?.id || index}
                sx={[
                  {
                    cursor: "pointer",
                    ...(row.disabled
                      ? {
                          "& .MuiTableCell-root": {
                            color: "grey",
                          },
                        }
                      : {}),
                  },
                  !(index % 2) && {
                    cursor: "pointer",
                    background: "#f5f5f5",
                  },
                  activeUserId === row?.id && {
                    cursor: "pointer",
                    // background: "#e7f5fe",
                    background: theme.palette.primaryBlack.main,
                  },
                ]}
                onClick={() => {
                  setActiveUserId(row.id);
                }}
                className={activeUserId === row?.id ? "selected" : null}
              >
                <TableCell>
                  <Stack direction="row" spacing={4} alignItems="center">
                    <MDBox mr={2} sx={{ color: "inherit" }}>
                      {row.name}
                    </MDBox>
                    {row.id === loginUser.userCompanyLinkId &&
                      checkPermission(PERMISSIONS_NAME.INPUT_KPIS, loginUser?.userPermission) && (
                        <CreateIcon
                          sx={{ cursor: "pointer", margin: "0 !important" }}
                          onClick={() => {
                            setIsUpdateKpiModalOpen(true);
                          }}
                        />
                      )}
                    {!row.inputKPIPermission ? (
                      <TooltipLight title="Does not input KPIs">
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "1.2rem", color: "#aaa", ml: 0 }}
                        >
                          unpublished
                        </span>
                      </TooltipLight>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell align="left">
                  <MDBox sx={{ ml: 2, color: "inherit" }}>
                    {row.inputKPIPermission
                      ? row?.averagePoints
                        ? numberFormatter(row?.averagePoints, true)
                        : "0.0"
                      : "-"}
                    <Trend prev={row?.averagePointTrend} curr={row?.averagePoints} type="percent" />
                  </MDBox>
                </TableCell>
                <TableCell align="center">
                  {row.inputKPIPermission
                    ? row?.totalAvg
                      ? numberFormatter(row?.totalAvg, true)
                      : "0.0"
                    : "-"}
                </TableCell>
                <TableCell align="center">
                  {row.inputKPIPermission
                    ? numberFormatter(parseInt(row?.totalCount || 0, 10))
                    : "-"}
                </TableCell>
                <TableCell align="center">
                  {row.inputKPIPermission
                    ? numberFormatter(parseInt(row?.totalPoint || 0, 10))
                    : "-"}
                </TableCell>
                <TableCell align="center">{row?.location || "-"}</TableCell>
                <TableCell align="center">{row?.team || "-"}</TableCell>
                <TableCell align="center">{row?.role || "-"}</TableCell>
                <TableCell align="center">{row?.reportsToName || "-"}</TableCell>
              </TableRowSlim>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <UpdateKpiModal
        selectedDate={selectedDate}
        open={isUpdateKpiModalOpen}
        setOpen={setIsUpdateKpiModalOpen}
        handleClose={handleDialogClose}
        dateFilter={dateFilter}
      />
    </MDBox>
  );
}

export default ListingTable;
